import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table'
import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { PlanTableProductCell } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableProductCell'
import { PlanTableSearchHeader } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableSearchHeader'
import { PlanTableTotalFooter } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableTotalFooter'
import { PlanTableCell } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableCell'
import { PlanTableMonthHeader } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableMonthHeader'
import { PlanTableMonthFooter } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableMonthFooter'
import {
  TableLoader,
  Wrapper,
} from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableData.styles.ts'
import { CircularProgress } from '@mui/material'
import AutoSizer from 'react-virtualized-auto-sizer'
import { usePlanTableConfig } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/usePlanTableConfig.tsx'
import { useSelector } from 'react-redux'
import {
  selectSearch,
  selectSort,
  selectTableProducts,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import { useShowScrollShadow } from '@/ui/screens/ReplenishmentPlan/PlanTable/PlanTableData/PlanTableDataPlainList/useShowScrollShadow.ts'
import { EmptyPlaceholderWrapper } from './PlanTableDataPlainList.styles.ts'
import { NothingFoundPlaceholder } from '@/ui/components/NothingFoundPlaceholder'

const PlanTableDataPlainList = () => {
  const data = useSelector(selectTableProducts)

  const search = useSelector(selectSearch)

  const sort = useSelector(selectSort)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const showShadow = useShowScrollShadow(tableContainerRef)

  useEffect(() => {
    scrollTo({ top: 0 })
  }, [sort])

  const scrollTo = ({ top, left }: { top?: number; left?: number }) => {
    const tableContainer = tableContainerRef.current

    if (!tableContainer) {
      return
    }

    if (top !== undefined) {
      tableContainer.scrollTop = top
    }

    if (left !== undefined) {
      tableContainer.scrollLeft = left
    }
  }

  const scrollToCurrentMonth = useCallback(() => {
    const tableContainer = tableContainerRef.current

    const left = tableContainer ? tableContainer.scrollWidth / 4 : null

    if (left) {
      scrollTo({ left })
    }
  }, [])

  const { dates, formatted, isTableLoading } = usePlanTableConfig()

  useEffect(() => {
    setTimeout(() => {
      scrollToCurrentMonth()
    })
  }, [scrollToCurrentMonth])

  useEffect(() => {
    if (!isTableLoading) {
      scrollToCurrentMonth()
    }
  }, [isTableLoading, scrollToCurrentMonth])

  const columns: MRT_ColumnDef<Product>[] = useMemo<MRT_ColumnDef<Product>[]>(
    () => [
      {
        accessorFn: (row) => row,
        id: 'name',
        header: 'Name',
        Cell: ({ cell: { getValue } }) => (
          <PlanTableProductCell product={getValue<Product>()} />
        ),
        Header: <PlanTableSearchHeader />,
        Footer: <PlanTableTotalFooter />,
        size: 350,
      },
      ...dates.map<MRT_ColumnDef<Product>>((date, index) => {
        const formattedMonth = formatted[index]

        return {
          accessorFn: (row) => row,
          id: formattedMonth,
          Cell: ({ cell: { getValue } }) => (
            <PlanTableCell product={getValue<Product>()} date={date} />
          ),
          header: formattedMonth,
          Header: <PlanTableMonthHeader date={date} />,
          Footer: <PlanTableMonthFooter date={date} />,
          size: 88,
        }
      }),
    ],
    [dates, formatted],
  )

  const table = useMaterialReactTable({
    columns,
    data,
    layoutMode: 'grid-no-grow',
    enableRowSelection: false,
    enableColumnOrdering: false,
    enableGlobalFilter: false,
    enableSorting: false,
    enableColumnActions: false,
    enablePagination: false,
    enableToolbarInternalActions: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableTableFooter:
      search.length === 0 && !isTableLoading && data.length !== 0,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableBatchRowSelection: false,
    enableCellActions: false,
    enableEditing: false,
    enableColumnPinning: true,
    enableRowVirtualization: true,
    rowVirtualizerOptions: {
      estimateSize: () => 88,
    },
    renderEmptyRowsFallback: () => <span />,
    initialState: {
      columnPinning: { left: ['name'] },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        position: 'relative',
        height: '100%',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '8px',
          background:
            'linear-gradient(270deg, #F2F6FA 0%, rgba(242, 246, 250, 0.00) 100%)',
          opacity: showShadow ? 1 : 0,
          transition: 'opacity 0.3s',
        },
      },
    },
    muiTableFooterCellProps: {
      sx: {
        width: '88px',
        padding: 0,
      },
    },
    muiTableHeadProps: {
      sx: {
        height: '48px',
        backgroundColor: (theme) => theme.palette.custom.white,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        padding: '0 !important',
        height: '100%',
        width: '88px',
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        '& .Mui-TableHeadCell-Content-Wrapper, & .Mui-TableHeadCell-Content-Labels':
          {
            width: '100%',
          },
        '&::before': {
          display: 'none',
        },
        '& .Mui-TableHeadCell-Content': {
          height: '100%',
        },
        backgroundColor: (theme) => theme.palette.custom.white,
      },
    },
    muiTableBodyRowProps: {
      sx: {
        borderBottom: (theme) => `2px solid ${theme.palette.custom.white}`,
        '&:hover td:after': {
          backgroundColor: (theme) => theme.palette.custom.white,
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: 0,
        border: 'none',
        '&:first-of-type': {
          marginRight: '-1px',
          borderRight: (theme) => `3px solid ${theme.palette.custom.white}`,
        },
      },
    },
    muiTableContainerProps: { ref: tableContainerRef, sx: { height: '100%' } },
    muiTableHeadRowProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    muiTableFooterProps: {
      sx: {
        outline: (theme) => `1px solid ${theme.palette.divider}`,
        zIndex: 0,
        '& .MuiTableCell-footer::before': {
          display: 'none',
        },
      },
    },
  })

  return (
    <Wrapper>
      {isTableLoading && (
        <TableLoader>
          <CircularProgress />
        </TableLoader>
      )}

      <AutoSizer>
        {({ width, height }) => (
          <div style={{ width, height }}>
            <MaterialReactTable table={table} />
          </div>
        )}
      </AutoSizer>

      {!isTableLoading && data.length === 0 && (
        <EmptyPlaceholderWrapper>
          <NothingFoundPlaceholder />
        </EmptyPlaceholderWrapper>
      )}
    </Wrapper>
  )
}

export default PlanTableDataPlainList
